// Strategic Primary Selection — judgment row, Conversions section.
// LLM-drafted verdict on whether the primary choices reflect the apparent business goal,
// with a collapsible "Actions Claude saw" table beneath for verification. Companion to the
// deterministic Primary Conversion Selection check (B1) in the same section.

import type { EvidenceProps } from './registry';

interface ActionRow {
  id?: string;
  name?: string;
  category?: string;
  primaryForGoal: boolean;
  includeInConversionsMetric: boolean;
  last30dConversions: number;
}

interface StrategicPrimaryShape {
  totalEnabled: number;
  primaryCount: number;
  secondaryCount: number;
  actions: ActionRow[];
}

const CATEGORY_LABEL: Record<string, string> = {
  PURCHASE: 'Purchase',
  SIGNUP: 'Sign-up',
  LEAD: 'Lead',
  ADD_TO_CART: 'Add to Cart',
  BEGIN_CHECKOUT: 'Begin Checkout',
  PAGE_VIEW: 'Page View',
  CONTACT: 'Contact',
  PHONE_CALL_LEAD: 'Call'
};

function categoryLabel(c: string | undefined): string {
  if (!c) return '—';
  return CATEGORY_LABEL[c] ?? c;
}

function formatVolume(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return n.toLocaleString();
}

export const StrategicPrimaryEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as StrategicPrimaryShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Actions Claude saw
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalEnabled} enabled · {e.primaryCount} primary · {e.secondaryCount} secondary
          </span>
        </h4>
        <table className='evidence__table evidence__table--strategic-primary'>
          <thead>
            <tr>
              <th>Conversion action</th>
              <th>Category</th>
              <th>Role</th>
              <th>Include in &ldquo;Conversions&rdquo;</th>
              <th className='evidence__th--right'>Conv (30d)</th>
            </tr>
          </thead>
          <tbody>
            {e.actions.map(r => (
              <tr key={r.id ?? r.name}>
                <td>
                  <div>{r.name ?? '(unnamed)'}</div>
                  {r.id && <div className='evidence__action-id'>{r.id}</div>}
                </td>
                <td>{categoryLabel(r.category)}</td>
                <td>
                  <span
                    className={`evidence__pill ${
                      r.primaryForGoal ? 'evidence__pill--primary' : 'evidence__pill--secondary'
                    }`}
                  >
                    {r.primaryForGoal ? 'Primary' : 'Secondary'}
                  </span>
                </td>
                <td>
                  <span
                    className={`evidence__pill ${
                      r.includeInConversionsMetric
                        ? 'evidence__pill--in-metric'
                        : 'evidence__pill--excluded'
                    }`}
                  >
                    {r.includeInConversionsMetric ? 'Counted' : 'Excluded'}
                  </span>
                </td>
                <td className='evidence__td--right'>{formatVolume(r.last30dConversions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
};
